import { action, observable, makeObservable } from 'mobx';
import * as Models from 'Models/Entities';
import * as Enums from 'Models/Enums';
import { refundStatus } from 'Models/Enums';
import { attribute, entity, IModelAttributes, Model } from 'Models/Model';
import { isNotNullOrUndefined } from 'Util/TypeGuards';

export interface IRefundEntityAttributes extends IModelAttributes {
	amount: number;
	status: refundStatus;
	providerRefundId: string;
	last4?: string;
	cardBrand?: string;
	note?: string;
	userId?: string;
	user?: Models.UserEntity | Models.IUserEntityAttributes;
	transactionId?: string;
	transaction?: Models.TransactionEntity | Models.ITransactionEntityAttributes;
}

@entity('RefundEntity', 'Refund')
export default class RefundEntity extends Model implements IRefundEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<RefundEntity, number>()
	public amount: number;

	@observable
	@attribute<RefundEntity, Enums.refundStatus>()
	public status: Enums.refundStatus;

	@observable
	@attribute<RefundEntity, string>()
	public providerRefundId: string;

	@observable
	@attribute<RefundEntity, string>()
	public last4: string;

	@observable
	@attribute<RefundEntity, string>()
	public cardBrand: string;

	@observable
	@attribute<RefundEntity, string>()
	public note: string;

	@observable
	@attribute()
	public transactionId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public transaction?: Models.TransactionEntity;

	@observable
	@attribute()
	public userId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public user?: Models.UserEntity;

	constructor(attributes?: Partial<IRefundEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IRefundEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (isNotNullOrUndefined(attributes.amount)) {
				this.amount = attributes.amount;
			}
			if (isNotNullOrUndefined(attributes.transactionId)) {
				this.transactionId = attributes.transactionId;
			}
			if (isNotNullOrUndefined(attributes.userId)) {
				this.userId = attributes.userId;
			}
			if (isNotNullOrUndefined(attributes.status)) {
				this.status = attributes.status;
			}
			if (isNotNullOrUndefined(attributes.providerRefundId)) {
				this.providerRefundId = attributes.providerRefundId;
			}
			if (isNotNullOrUndefined(attributes.last4)) {
				this.last4 = attributes.last4;
			}
			if (isNotNullOrUndefined(attributes.cardBrand)) {
				this.cardBrand = attributes.cardBrand;
			}
			if (isNotNullOrUndefined(attributes.note)) {
				this.note = attributes.note;
			}
			if (attributes.user !== undefined) {
				if (attributes.user === null) {
					this.user = attributes.user;
				} else if (attributes.user instanceof Models.UserEntity) {
					this.user = attributes.user;
					this.userId = attributes.user.id;
				} else {
					this.user = new Models.UserEntity(attributes.user);
					this.userId = this.user.id;
				}
			}
			if (attributes.transaction !== undefined) {
				if (attributes.transaction === null) {
					this.transaction = attributes.transaction;
				} else if (attributes.transaction instanceof Models.TransactionEntity) {
					this.transaction = attributes.transaction;
					this.transactionId = attributes.transaction.id;
				} else {
					this.transaction = new Models.TransactionEntity(attributes.transaction);
					this.transactionId = this.transaction.id;
				}
			}
		}
	}
}
